/**
 * <s-complete-look> — adds the current product + a matching Fluffy partner to the cart in one go.
 *
 * Reads the selected main variant from the product form's input[name="id"] (kept current by s-variant-picker),
 * pre-selects the partner size that matches the main size, shows the set price on the button and
 * adds both lines with Shopify.actions.updateCart, then confirms via the cart island or opens the cart.
 */
class SCompleteLook extends HTMLElement {
  #ac;
  #mo;
  #prices = {};
  #lastMainSize = '';

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    try {
      this.#prices = JSON.parse(this.querySelector('[data-look-main-prices]')?.textContent || '{}');
    } catch {
      this.#prices = {};
    }

    const form = this.#form();
    if (form) {
      this.#mo = new MutationObserver(() => this.#refresh());
      this.#mo.observe(form, { childList: true, subtree: true });
      form.addEventListener('change', () => this.#refresh(), opts);
    }
    this.addEventListener('change', () => this.#refresh(false), opts);
    // Size chips drive the hidden select (source of truth for price and add to cart).
    this.addEventListener('click', (event) => {
      const chip = event.target instanceof Element ? event.target.closest('[data-look-chip]') : null;
      if (!chip || chip.disabled) return;
      const select = chip.closest('[data-look-partner]')?.querySelector('[data-look-size]');
      if (!select || select.value === chip.dataset.lookChip) return;
      select.value = chip.dataset.lookChip;
      this.#refresh(false);
    }, opts);
    this.querySelector('[data-look-add]')?.addEventListener('click', () => this.#add(), opts);
    this.#refresh();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#mo?.disconnect();
  }

  #form() {
    return this.parentElement?.querySelector(this.dataset.form || '[data-product-form]') || null;
  }

  #mainVariantId() {
    const input = this.#form()?.querySelector('input[name="id"]');
    return input ? String(input.value) : '';
  }

  #mainAvailable() {
    return Boolean(this.#form()?.querySelector('button[type="submit"][name="add"]'));
  }

  #mainSize() {
    const checked = [...(this.#form()?.querySelectorAll('s-variant-picker input:checked') || [])];
    // Size is the option whose values look like sizes; fall back to the last checked option.
    const size = checked.find((input) => /^(xxs|xs|s|m|l|xl|xxl|[2-5]xl)$/i.test(input.value.trim()));
    return (size || checked[checked.length - 1])?.value.trim() || '';
  }

  #activePartner() {
    return this.querySelector('[data-look-partner]:has(input[type="radio"]:checked)');
  }

  #format(cents) {
    try {
      return new Intl.NumberFormat(this.dataset.locale || 'de', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  #refresh(syncSize = true) {
    const mainSize = this.#mainSize();
    // Match the partner size to the main size whenever the main size changes.
    if (syncSize && mainSize && mainSize !== this.#lastMainSize) {
      this.#lastMainSize = mainSize;
      this.querySelectorAll('[data-look-size]').forEach((select) => {
        const match = [...select.options].find((option) => !option.disabled && option.dataset.size?.trim().toLowerCase() === mainSize.toLowerCase());
        if (match) select.value = match.value;
      });
    }

    this.querySelectorAll('[data-look-size]').forEach((select) => {
      select.closest('[data-look-partner]')?.querySelectorAll('[data-look-chip]').forEach((chip) => {
        chip.setAttribute('aria-pressed', String(chip.dataset.lookChip === select.value));
      });
    });

    const button = this.querySelector('[data-look-add]');
    if (!button) return;
    const partner = this.#activePartner();
    const option = partner?.querySelector('[data-look-size]')?.selectedOptions[0];
    const mainPrice = Number(this.#prices[this.#mainVariantId()] || 0);
    const partnerPrice = Number(option?.dataset.price || 0);
    const ok = this.#mainAvailable() && option && !option.disabled;

    button.disabled = !ok;
    // Same maths as the Sets page and the per-colour automatic discounts: 10 % of the whole set.
    const full = mainPrice + partnerPrice;
    // Only pairs with an active set discount (data-look-discount, set in Liquid) show the reduced price.
    const discounted = Boolean(partner?.hasAttribute('data-look-discount'));
    const setPrice = discounted ? full - Math.round(full * 0.1) : full;
    const key = ok ? `${full}|${setPrice}` : 'sold-out';
    if (button.dataset.key === key) return;
    button.dataset.key = key;
    if (!ok) {
      button.textContent = this.dataset.soldOut;
      return;
    }
    if (!discounted) {
      button.textContent = `${this.dataset.label} · ${this.#format(full)}`;
      return;
    }
    const compare = document.createElement('s');
    compare.textContent = this.#format(full);
    button.replaceChildren(`${this.dataset.label} · ${this.#format(setPrice)}`, compare);
  }

  async #add() {
    const button = this.querySelector('[data-look-add]');
    const error = this.querySelector('[data-look-error]');
    const partnerId = this.#activePartner()?.querySelector('[data-look-size]')?.value;
    const mainId = this.#mainVariantId();
    if (!button || !partnerId || !mainId || !window.Shopify?.actions?.updateCart) return;

    button.setAttribute('aria-busy', 'true');
    if (error) error.hidden = true;
    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [{ merchandiseId: mainId, quantity: 1 }, { merchandiseId: partnerId, quantity: 1 }] },
        { event: { context: 'product', detail: { source: 's-complete-look' } } }
      );
      const message = result?.userErrors?.[0]?.message;
      if (message) throw new Error(message);
      const island = document.querySelector('s-cart-island');
      const shown = island && typeof island.show === 'function' ? await island.show(partnerId) : false;
      if (!shown) await window.Shopify.actions.openCart?.();
    } catch (err) {
      if (error) {
        error.textContent = err?.message || this.dataset.errorMessage || '';
        error.hidden = false;
      }
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('s-complete-look')) {
  customElements.define('s-complete-look', SCompleteLook);
}
